/**
 * Safely write text to the clipboard, catching errors when the document
 * is not focused or the Clipboard API is unavailable.
 * Returns `true` on success, `false` on failure.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Silently ignore clipboard errors (e.g. NotAllowedError when
    // the document is not focused).
    return false;
  }
}

/**
 * Copy both a rich (`text/html`) and a plain (`text/plain`) representation so
 * rich targets (Sheets, Docs, Notion) keep structure while plain editors get
 * the fallback. Falls back to plain-text-only when ClipboardItem is
 * unavailable or the write is rejected.
 */
export async function copyRichText(
  html: string,
  text: string,
): Promise<boolean> {
  const ClipboardItemCtor =
    typeof ClipboardItem !== "undefined"
      ? (ClipboardItem as typeof ClipboardItem & {
          supports?: (type: string) => boolean;
        })
      : undefined;

  // Gate on ClipboardItem.supports so an unwritable text/html never triggers a
  // rejected clipboard.write: Safari drops transient user activation once such
  // a write is awaited, which would also doom the plain-text fallback below.
  if (
    ClipboardItemCtor &&
    typeof navigator.clipboard?.write === "function" &&
    (typeof ClipboardItemCtor.supports !== "function" ||
      ClipboardItemCtor.supports("text/html"))
  ) {
    try {
      await navigator.clipboard.write([
        new ClipboardItemCtor({
          "text/html": new Blob([html], { type: "text/html" }),
          "text/plain": new Blob([text], { type: "text/plain" }),
        }),
      ]);
      return true;
    } catch {
      // Fall back to plain text below.
    }
  }
  return copyToClipboard(text);
}
